// iter 163: manual category assignment for products the LLM couldn't
// classify. Lists every 'unknown' product with photo + description, offers
// a category dropdown per row, then bulk-submits the mapping.
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { api } from "../lib/api";

const CATEGORIES = ["bedding", "bath", "kitchen", "rug", "window"];

export function ManualCategorizeDialog({ open, onOpenChange, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  // rowId -> pending category selection
  const [pending, setPending] = useState({});

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setPending({});
    api.get("/products/unknowns")
      .then(({ data }) => setRows(data.items || []))
      .catch((e) => {
        toast.error(`Failed to load unknowns: ${e.response?.data?.detail || e.message}`);
        onOpenChange(false);
      })
      .finally(() => setLoading(false));
  }, [open, onOpenChange]);

  const handleSubmit = async () => {
    const updates = Object.entries(pending)
      .filter(([, cat]) => !!cat)
      .map(([product_id, category]) => ({ product_id, category }));
    if (updates.length === 0) {
      toast.error("No categories selected");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/products/bulk-category", { updates });
      toast.success(`Updated ${data.updated} product(s)`);
      onSaved?.(data.updated);
      // Remove updated rows from the list
      const updatedIds = new Set(updates.map((u) => u.product_id));
      setRows((prev) => prev.filter((r) => !updatedIds.has(r.id)));
      setPending({});
      if (data.updated === rows.length) onOpenChange(false);
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const pendingCount = Object.values(pending).filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col" data-testid="manual-categorize-dialog">
        <DialogHeader>
          <DialogTitle>Manual Category Assignment</DialogTitle>
          <DialogDescription>
            {loading ? "Loading unknowns…" : `${rows.length} product(s) currently marked unknown. Pick a category on the rows you want to fix, then click Save.`}
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {loading ? null : rows.length === 0 ? (
            <p className="text-sm text-gray-500 py-8 text-center">🎉 No unknowns! Everything is classified.</p>
          ) : (
            <div className="space-y-2">
              {rows.map((r) => (
                <div key={r.id} data-testid={`unknown-row-${r.id}`} className="flex items-start gap-3 border border-gray-200 rounded-lg p-3 hover:border-brand-300 transition-colors">
                  <div className="w-16 h-16 flex-shrink-0 bg-gray-100 rounded overflow-hidden">
                    {r.photo_url ? (
                      <img src={r.photo_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">No img</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="font-mono font-semibold text-gray-800">{r.item_no || r.serial_number}</span>
                      {r.contract_no && <span className="text-xs font-mono text-gray-500">· {r.contract_no}</span>}
                      {r.customer_product_no && <span className="text-xs font-mono text-gray-500">· {r.customer_product_no}</span>}
                    </div>
                    <p className="text-sm text-gray-700 truncate mt-0.5">{r.description || <em className="text-gray-400">(no description)</em>}</p>
                  </div>
                  <Select
                    value={pending[r.id] || ""}
                    onValueChange={(v) => setPending((p) => ({ ...p, [r.id]: v }))}
                  >
                    <SelectTrigger className="w-32" data-testid={`unknown-row-select-${r.id}`}>
                      <SelectValue placeholder="Pick" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter className="border-t border-gray-100 pt-3 mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="manual-categorize-cancel">Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={saving || pendingCount === 0}
            data-testid="manual-categorize-save"
          >
            {saving ? "Saving…" : `Save ${pendingCount || ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
